
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../utils/api";
import CatalogListGrid from "../../../common/catalog/CatalogListGrid";
import CatalogListSection from "../../../common/catalog/CatalogListSection";
import CatalogEmptyState from "../../../common/catalog/CatalogEmptyState";
import {
	CATALOG_PAGE_SIZE,
	catalogPageParams,
} from "../../../common/catalog/catalogPageSize";
import { getCatalogEmptyCopy } from "../../../common/catalog/catalogEmptyCopy";
import Paginator from "../../../common/paginator/Paginator";
import Skeleton from "../../../common/loading/Skeleton";
import Doctor from "./Doctor";

function displayMaxPage(totalCount) {
	const total = Number(totalCount);
	if (!Number.isFinite(total) || total <= 0) {
		return 1;
	}
	if (total <= CATALOG_PAGE_SIZE) {
		return 1;
	}
	return Math.ceil(total / CATALOG_PAGE_SIZE);
}

export default function DoctorSection({ isDesktop, query, expertiseId }) {
	const navigate = useNavigate();
	const [doctors, setDoctors] = useState([]);
	const [page, setPage] = useState(1);
	const [maxPageNum, setMaxPageNum] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const queryParam = (query ?? "").trim();
	const expertiseIdParam =
		expertiseId != null && String(expertiseId).trim() !== ""
			? String(expertiseId)
			: "";

	useEffect(() => {
		let cancelled = false;

		async function loadDoctors() {
			setIsLoading(true);
			setErrorMessage("");
			try {
				const result = await api.doctorSearch({
					query: queryParam,
					expertise_id: expertiseIdParam,
					...catalogPageParams(page),
				});
				if (cancelled) return;

				const list = result?.doctors ?? [];
				const totalCount = Number(result?.total_count);
				let maxPage;
				if (Number.isFinite(totalCount) && totalCount >= 0) {
					maxPage = displayMaxPage(totalCount);
				} else {
					const apiMax = Math.max(1, Number(result?.max_page) || 1);
					if (apiMax > 1 && page === 1 && list.length < CATALOG_PAGE_SIZE) {
						maxPage = 1;
					} else {
						maxPage = apiMax;
					}
				}

				if (page > maxPage) {
					setMaxPageNum(maxPage);
					setPage(maxPage);
					return;
				}

				if (list.length === 0 && page > 1) {
					setMaxPageNum(Math.max(1, page - 1));
					setPage(Math.max(1, page - 1));
					return;
				}

				setDoctors(list);
				setMaxPageNum(maxPage);
			} catch (err) {
				if (cancelled) return;
				if (err && err.data) {
					setErrorMessage(err.data.message);
				}
				setDoctors([]);
				setMaxPageNum(1);
			} finally {
				if (!cancelled) {
					setIsLoading(false);
				}
			}
		}

		loadDoctors();
		return () => {
			cancelled = true;
		};
	}, [page, queryParam, expertiseIdParam]);

	const emptyCopy = getCatalogEmptyCopy({
		itemSingular: "درمانگر",
		query: queryParam,
		hasExtraFilter: Boolean(expertiseIdParam),
		extraFilterLabel: "این تخصص",
	});

	const showPagination = maxPageNum > 1;

	const pagination = showPagination ? (
		<Paginator
			currentPage={page}
			maxPageNum={maxPageNum}
			isLoading={isLoading}
			onChange={(newPage) => {
				setPage(newPage);
			}}
		/>
	) : null;

	return (
		<CatalogListSection pagination={pagination}>
			{doctors.length === 0 && isLoading && (
				<Skeleton variant="grid" type="doctor" count={8} />
			)}
			{doctors.length >= 1 && (
				<CatalogListGrid isDesktop={isDesktop}>
					{doctors.map((doctor, index) => (
						<Doctor key={doctor.id} profile={doctor} index={index} />
					))}
				</CatalogListGrid>
			)}
			{doctors.length === 0 && !isLoading && !errorMessage && (
				<CatalogEmptyState
					message={emptyCopy.message}
					hint={emptyCopy.hint}
					actionLabel={emptyCopy.showClear ? "پاک کردن فیلترها" : undefined}
					onAction={
						emptyCopy.showClear
							? () => navigate("/doctor_list/")
							: undefined
					}
				/>
			)}
			{errorMessage && (
				<div className="text-center font-Vazir-400 text-[#cc0000]">
					{errorMessage}
				</div>
			)}
		</CatalogListSection>
	);
}
